import compareVersion from "compare-versions";
import type { ZodSchema } from "zod";

import type { DesignerNodeData } from "@skilitics/nautilus-core-designer-data";
import {
  assert,
  extractMultilingualProperties,
  isMultilingualProperty,
  monolingualizeConfig,
  stripUnsupportedTranslationsCreate,
} from "@skilitics/nautilus-core-designer-data";

import type {
  GetManifestByTypeId,
  GetMigrateFromManifestByTypeId,
  MigrateDesignerNode,
  MigrateOptions,
  NodeManifestAny,
} from "../../types/index.ts";
import type { CreateNodeData, CreatePlaceholderData } from "../creation/index.ts";
import { fromMultilingualCreate, toMultilingualCreate } from "./multilingualHelpers.ts";

interface CreateOptions {
  getManifest: GetManifestByTypeId;
  getMigrateFromManifest: GetMigrateFromManifestByTypeId;
  createNode: CreateNodeData;
  createPlaceholder: CreatePlaceholderData;
  disableSchemaValidation?: boolean; // really for simplifying some tests that were written without valid configs
  multilingual: {
    supportedLanguageCodes: string[];
    primaryLanguageCode: string;
  };
}

export const migrateNodeCreate = ({
  getManifest,
  getMigrateFromManifest,
  createNode,
  createPlaceholder,
  disableSchemaValidation,
  multilingual: { primaryLanguageCode, supportedLanguageCodes },
}: CreateOptions) => {
  const options: Omit<MigrateOptions, "context"> = {
    createNode,
    createPlaceholder,
    isMultilingual: isMultilingualProperty,
    toMultilingual: toMultilingualCreate(primaryLanguageCode),
    fromMultilingual: fromMultilingualCreate(primaryLanguageCode),
  };

  const stripUnsupportedLanguages = stripUnsupportedTranslationsCreate({ supportedLanguageCodes });

  const validateConfig = (
    { typeId, designer: { multilingual }, version: currentVersion }: NodeManifestAny,
    node: DesignerNodeData,
    schema?: ZodSchema,
  ) => {
    const multilingualProperties = extractMultilingualProperties(node.config);
    const potentiallyMultilingualProperties = Object.keys(multilingual ?? {});
    for (const { path, translations } of multilingualProperties) {
      if (!(primaryLanguageCode in translations)) {
        // bit tricky for node designer developers as they don't know/control the primary language of consumers
        throw new Error(
          `Property "${path}" in migration of ${typeId} ${node.version} is multilingual but does not include module's primary translation (${primaryLanguageCode}).`,
        );
      }
      if (node.version === currentVersion && !potentiallyMultilingualProperties.includes(path)) {
        throw new Error(
          `Property "${path} in migration of ${typeId} ${node.version} is multilingual but not listed in "multilingual" section`,
        );
      }
    }
    if (disableSchemaValidation) return;
    schema?.parse(monolingualizeConfig(node.config, { lang: primaryLanguageCode }));
  };

  const migrateNode = (
    input: MigrateDesignerNode,
    context: MigrateOptions["context"],
  ): MigrateDesignerNode | undefined | null => {
    const manifest = getManifest(input.typeId, { ignoreIfNotFound: true }) ?? getMigrateFromManifest(input.typeId);
    const { version: currentVersion, designer } = manifest;

    assert(
      input.version !== currentVersion || input.typeId !== manifest.typeId,
      `Migrate should not be called when version is current ${input.typeId} ${input.version}.`,
    );

    const { configSchemas } = designer;
    validateConfig(manifest, input, configSchemas?.[input.version]);

    const oldVersion = input.version || "0.1.0"; // default version

    const output = designer.migrate?.(
      {
        ...input,
        version: oldVersion,
      } as MigrateDesignerNode,
      {
        ...options,
        context,
      },
    );

    if (output) {
      if (!output.version) {
        throw new Error(`Migration of node ${input.typeId} ${input.version ?? ""} has returned no version.`);
      }

      if (compareVersion(oldVersion, output.version) > 0 && output.typeId === input.typeId) {
        throw new Error(
          `Migration of node ${input.typeId} ${input.version ?? ""} has returned older version ${output.version}.`,
        );
      }
      if (output.version !== currentVersion && output.typeId === input.typeId) {
        if (output.version === input.version) {
          throw new Error(`Migration of node ${input.typeId} returned same version ${input.version ?? ""}.`);
        }
        // NOTE: this will not work for child restructures, deletes and moves will not be picked up and applied
        // this needs to be pushed up a level probably
        return migrateNode(output, context);
      }

      validateConfig(manifest, output, configSchemas?.[output.version] ?? configSchemas?.current);

      output.config = stripUnsupportedLanguages(output.config);
    }

    return output;
  };

  return migrateNode;
};

export type MigrateNode = ReturnType<typeof migrateNodeCreate>;
